import { BusinessModel } from 'enums/businessModel';
import { Currency } from 'enums/currency';
import { DemandResponseState } from 'enums/demandState';
import { ExtraUnitType } from 'enums/extraUnitTypes';
import { OfferState } from 'enums/offerState';
import {
  SupportedDailyWeeklyRentalTypes,
  SupportedHourlyRentalTypes,
} from 'enums/supportedRentalTypes';
import { VehiclePriceUnit } from 'enums/vehiclePriceUnit';
import { VehicleType } from 'enums/vehicleTypes';
import { DropdownOptionWithDescription } from 'types/types';

// #region demand ui
export enum DemandState {
  RESPONDED_AVAILABILITY = 'RESPONDED_AVAILABILITY',
  RESPONDED_TENDER = 'RESPONDED_TENDER',
  RESPONDED_NEW_DATE = 'RESPONDED_NEW_DATE',
  CANCELLED = 'CANCELLED',
  NOT_RESPONDED = 'NOT_RESPONDED',
  WAITING = 'WAITING',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
  DENIED = 'DENIED',
}

export enum DemandType {
  TENDER = 'TENDER',
  AVAILABILITY = 'AVAILABILITY',
  RESERVATION = 'RESERVATION',
}

export type SelectedExtraUnits = Record<number, string | null>;

export interface VehicleBasedDemandState {
  harbor: string;
  endHarbor: string;
  date: Date | null;
  duration: number;
  numberOfPeople: string;
  selectedMeal: number | null;
  selectedPackage: number | null;
  selectedServices: number[];
  selectedMealsUnits: Record<number, string>;
  selectedPackagesUnits: SelectedExtraUnits;
  selectedServicesUnits: SelectedExtraUnits;
  isSwimmingTour: boolean;
}

export interface VehicleBasedDailyWeeklyDemandState {
  harbor: string;
  endHarbor: string;
  startDate: Date | null;
  endDate: Date | null;
  numberOfPeople: string;
  selectedServices: number[];
  selectedServicesUnits: SelectedExtraUnits;
  demandVariant: SupportedDailyWeeklyRentalTypes;
}

export interface DemandVehicle {
  id: number;
  rating: number;
  type: VehicleType;
  commentCount: number;
  images: string[];
  vehiclePrioritizedConditions: string[];
  brand: string;
  length: number;
  businessModel: BusinessModel;
}

export interface Offer {
  id: number;
  startDate: Date | null;
  endDate: Date | null;
  totalPrice: number;
  prepaymentPrice: number;
  priceToPayOnBoard: number;
  currency: Currency;
  isTimeSuggested: boolean;
  vehicle: DemandVehicle;
  paymentInfo: PaymentInfo;
}

export interface Extra {
  name: string;
  price?: number;
  unit?: ExtraUnitType;
  count?: number;
}

export interface Extras {
  meals: Extra[];
  services: Extra[];
  packages: Extra[];
}

export interface Demand {
  id: number;
  demandType: DemandType;
  ratingDone?: boolean;
  organization: string;
  organizationTRName: string;
  numberOfPeople: number;
  creationDate: Date;
  state: DemandState;
  harbor: string;
  endHarbor: string;
  region: string;
  regionTRName: string;
  startDate: Date;
  endDate: Date;
  extras: Extras;
  offer: Offer[];
  vehicle?: DemandVehicle;
  paymentInfo?: PaymentInfo;
  contactInfo?: ContactInfo;
  hourlyVariant: SupportedHourlyRentalTypes;
  dwVariant: SupportedDailyWeeklyRentalTypes;
  freeCancellable?: boolean;
}

export interface TenderFormState {
  harbor: number;
  numberOfPeople: number;
  startDate: Date;
  duration: number;
  extras: number[];
  packages: number | null;
  meal: number | null;
  note: string;
}
// #endregion

// #region demand post
export interface IdValuePair {
  value: number;
  id: number;
  description: string;
}

export interface BasePriceRaises {
  seasonalIncrease: number;
  specialDayIncrease: number;
}

export interface CostWithHour {
  hours: number;
  hourCost: number;
  totalCost: number;
}

export type CostPerPeriod = Record<string, CostWithHour>;
export interface Discounts {
  couponDiscount: number;
}

export interface PaymentInfo {
  currency: Currency;
  totalAmount: number;
  prePaymentAmount: number;
  amountToPayToPartnerFromPrePayment: number;
  commissionAmountFromPrePayment: number;
  priceToPayOnBoard: number;
  servicePricings: IdValuePair[];
  mealPricings: IdValuePair[];
  basePriceRaises: BasePriceRaises;
  eventTimePeriodAdditionalCost: number;
  costPerPeriod: CostPerPeriod;
  basePrice: number;
  vehicleCostPerUnit: number;
  priceUnit: VehiclePriceUnit;
  discountedTotalAmount: number;
  discounts: Discounts;
}

export type SelectedServices = Record<number, number>;
export type SelectedMeals = Record<number, number>;

export interface DateTimeRange {
  startTime: string;
  endTime: string;
}

export interface CreateDemandPayload {
  selectedOrganizationId?: number;
  selectedServices: SelectedServices;
  selectedMeals: SelectedMeals;
  startHarborId: number;
  finishHarborId: number;
  passengerCount: number;
  notes?: string;
  targetVehicleId?: number;
  dwVariant?: SupportedDailyWeeklyRentalTypes;
  hourlyVariant?: SupportedHourlyRentalTypes;
  dateTimeRange: DateTimeRange;
  paymentInfo?: PaymentInfo;
  platform: 'WEB';
  source: 'API' | 'MESSAGE';
  initialMessage?: string;
}

export interface CreateDemandWithMessagePayload extends CreateDemandPayload {
  message?: string;
  demandId?: number;
}
// #endregion

export interface CreateDemandResponse {
  offerId: number | null;
  demandId: number;
}

// #region demand get
export interface DemandResponseComment {
  title: string;
  content: string;
  rate: number;
}

export interface DemandResponseRatingScoresAverage {
  hygiene: number;
  service: number;
  communication: number;
  pricePerformance: number;
}

export interface DemandResponseRatingComments {
  commentId: number;
  title: number;
  content: number;
  rate: DemandResponseRatingScoresAverage;
}

export interface DemandResponseRatingVehicleAverage {
  commentCount: number;
  overallAverage: number;
  averageBundle: DemandResponseRatingScoresAverage;
}

export interface DemandResponseRating {
  vehicleAverage: DemandResponseRatingVehicleAverage;
  comments: DemandResponseRatingComments;
}

export interface DemandResponseVehicle {
  id: number;
  rating: DemandResponseRating;
  type: VehicleType;
  images: string[];
  desription: string;
  freeCancellationPeriod: number;
  usageConditions: DropdownOptionWithDescription[];
  brandName: string;
  vehicleLength: number;
  businessModel: BusinessModel;
}

export interface DemandResponseOffer {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  suggestedDateTimeRange: DateTimeRange;
  paymentInfo: PaymentInfo;
  vehicle: DemandResponseVehicle;
  state: OfferState;
}

export interface DemandResponsePrice {
  currency: Currency;
  amount: number;
  unit: VehiclePriceUnit;
}

export interface DemandResponseExtraPrice {
  currency: Currency;
  amount: number;
  unit: ExtraUnitType;
}

export interface DemandResponseMeal {
  id: number;
  title: string;
  description: string;
  demandId: number;
  count: number;
  price: DemandResponseExtraPrice;
}

export interface DemandResponseService extends DemandResponseMeal {
  unit: ExtraUnitType;
  type: string;
}

export interface DemandResponseDemand {
  id: number;
  startHarbor: string;
  finishHarbor: string;
  region: string;
  regionTRName: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  passengerCount: number;
  state: DemandResponseState;
  badgeState: DemandState;
  organization: string;
  organizationTRName: string;
  meals: DemandResponseMeal[];
  services: DemandResponseService[];
  packages: DemandResponseService[];
  targetVehicle: DemandResponseVehicle;
  paymentInfo: PaymentInfo;
  brand: string;
  length: number;
  dwVariant: SupportedDailyWeeklyRentalTypes;
  hourlyVariant: SupportedHourlyRentalTypes;
}

export interface DemandGetResponse {
  demand: DemandResponseDemand;
  offers: DemandResponseOffer[];
}
// #endregion

// #region reservation
export enum ReservationState {
  PRE_PAYMENT_PENDING = 'PRE_PAYMENT_PENDING',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  CANCELLED = 'CANCELLED',
  ON_GOING = 'ON_GOING',
  WAITING_DUE_DATE = 'WAITING_DUE_DATE',
  DONE = 'DONE',
}

export interface ReservationResponseReservation {
  id: number;
  ratingDone: boolean;
  startHarbor: string;
  finishHarbor: string;
  region: string;
  regionTRName: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  passengerCount: number;
  state: ReservationState;
  organization: string;
  organizationTRName: string;
  meals: DemandResponseMeal[];
  services: DemandResponseService[];
  packages: DemandResponseService[];
  contactInfo: ContactInfo;
  dwVariant: SupportedDailyWeeklyRentalTypes;
  hourlyVariant: SupportedHourlyRentalTypes;
  freeCancellable: boolean;
}

export interface ContactInfo {
  name: string;
  phoneNumber: string;
}

export interface ReservationGetResponse {
  reservation: ReservationResponseReservation;
  offer: DemandResponseOffer;
}

// #endregion

// #region calculateAmount Response
export namespace CalculatePricing {
  export interface MappedResponse extends PaymentInfo {
    mealPrice: number;
    extraPrice: number;
  }
  export interface MappedResponseWithDuration extends MappedResponse {
    duration: number;
  }
}
// #endRegion

export type DemandCardType = 'offer' | 'reservation';

export type DemandSubmitType = 'default' | 'message' | 'message-update';
